// Court/county identity — the repo's ONE canonical comparator, plus the
// CaseCourtBinding contract that consumes it (SOR-182).
//
// Three value spaces name the same court, and all three reach a comparison:
//   - county DISPLAY name — `orderInfo.county`, which `GET /efiling/courts`
//     returns as `county: ct.County` and InlineCourtSelector maps straight into
//     the option value ("Madera", "Los Angeles")
//   - JTI court code — a lowercase slug ("madera", "los-angeles"), what the case
//     record's CaseCourt/OrganizationIdentification/IdentificationID carries and
//     what `resolveJtiCourt` keys on
//   - OCR extraction slug — also lowercase-hyphenated (Walter R85)
// Comparing any two of them raw is the `case-county-mismatch` false positive
// that fires on every legitimate same-court subsequent filing.
//
// This module is a LEAF by design: its only import is the plain county table
// below, so `efiling/validate.ts` (shared by client and server) can consume it
// without dragging the JTI and Tyler adapter registries in behind it. The
// import is on `efm-reference/counties.js` DIRECTLY rather than that folder's
// barrel, which re-exports adapter-adjacent modules. That also makes the
// reverse edge — `efm/index.ts` re-exporting `countySlug` from here — acyclic.
import { CA_COUNTIES } from '../../efm-reference/counties.js';
/**
 * Canonicalize a court/county identity for comparison: lowercased, each run of
 * non-alphanumeric characters collapsed to one hyphen, leading/trailing hyphens
 * trimmed. "Madera" / "madera" / "MADERA" all yield `madera`; "Los Angeles" and
 * "los-angeles" both yield `los-angeles`.
 *
 * The single authority for the CASE-vs-FILING-COURT decision. Every name for
 * that decision resolves here: `countySlug` in `efm/index.ts` and `countySlug`
 * in the user-portal's `lib/county-slug.ts` are re-exports, and api-gateway's
 * `resolveEfmVendor` (`efm-client/index.ts`) and `initiationLeadOverrides`
 * (`reference/filter-document-types.ts`) call this function directly — those two
 * were inline copies stranded on a `\s+` algorithm until SOR-189 retired them.
 * No further implementation of it may be added.
 *
 * The claim is scoped to that decision on purpose, because one other county
 * comparison in the repo does NOT route here: the Tyler branch of api-gateway's
 * court-jurisdictions handler (`features/efiling/routes.ts`, the
 * `resolveEfmVendor(county) === 'tyler'` block) matches the `county` query param
 * against the courts dataset's `ct.County` with its own
 * `.toLowerCase().replace(/[\s-]/g, '')`. That is not a latent divergence — it
 * normalizes BOTH sides with the same algorithm, so the comparison is
 * self-consistent, and no CA county display name carries punctuation that could
 * separate the two algorithms. It is also not a free cutover: that algorithm
 * STRIPS separators ("Los Angeles" -> "losangeles") where this one COLLAPSES
 * them to a hyphen ("los-angeles"), so the two agree on which pairs match only
 * while both sides carry their separators. Moving it therefore changes the match
 * relation for a separator-less input and belongs in its own change with its own
 * test, not in a docstring edit.
 *
 * The re-exports are pinned by `court-identity.test.ts`; the two api-gateway
 * call sites live in the other package, so they are pinned from there by
 * `apps/api-gateway/src/__tests__/county-slug-cutover.test.ts`, whose county
 * forms are deliberately punctuated — the 58 CA county display names carry none,
 * so only a punctuated input can tell the two algorithms apart.
 */
export function courtIdentityKey(value) {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        // Quantifier-free by design. The collapse above emits maximal runs as a
        // SINGLE hyphen, so no two hyphens can ever be adjacent here and `-+` could
        // only ever match the one character `-` does. Writing it `/^-+|-+$/g` is
        // therefore identical in output but reads as unbounded backtracking to
        // CodeQL (js/polynomial-redos). The two forms are coupled: if the collapse
        // above ever stops collapsing runs, this line stops trimming fully.
        .replace(/^-|-$/g, '');
}
/**
 * The county identities a recorded court value can be COMPARED against — the
 * canonical eFiling county set, keyed the one canonical way.
 *
 * This set is what makes `foreign-court` a decidable accusation rather than a
 * guess. `orderInfo.county` always comes from this value space (the courts API
 * returns county display names, and `CA_COUNTIES` is the offline fallback the
 * two county selects share), so a recorded court that canonicalizes into this
 * set is directly comparable with the filing court and a disagreement between
 * two members is a real cross-court attach. A recorded value OUTSIDE it is in
 * some other value space entirely and a `!==` against the filing court says
 * nothing — see `unrecognized-court` below.
 */
const COUNTY_IDENTITY_KEYS = new Set(CA_COUNTIES.flatMap((c) => [courtIdentityKey(c.code), courtIdentityKey(c.name)]));
/**
 * True when `key` (already canonical) names a county in the filing court's own
 * value space. Exported so the api-gateway can flag a court identity it cannot
 * place — see the observability note on `parseCaseResponse`'s caller.
 */
export function isCountyIdentityKey(key) {
    return COUNTY_IDENTITY_KEYS.has(key);
}
/**
 * Whether a provenance ATTRIBUTES the recorded value to a court — the sole gate
 * on the positive `matches-court` claim below.
 *
 * The `default` branch is a compile-time exhaustiveness check on purpose: a new
 * provenance member must be classified here deliberately, rather than silently
 * falling to `absent` and quietly un-asserting a binding some writer believed it
 * had recorded.
 */
function isAttributed(provenance) {
    switch (provenance) {
        case 'case-record':
        case 'served-court':
            return true;
        case undefined:
            return false;
        default: {
            const exhaustive = provenance;
            return Boolean(exhaustive);
        }
    }
}
/**
 * Classify a draft's CaseCourtBinding. Total over every input pair:
 *
 *   - `absent` — nothing is asserted about the case's court. Two ways in: no
 *     recorded value at all (the record carried no court identification —
 *     `OrganizationIdentification` is `minOccurs="0"` — or the value
 *     canonicalizes to nothing), or a value that AGREES with the filing court
 *     but cannot be attributed to the case record. An absent binding never
 *     matches, and it grace-passes: we cannot retroactively know which court an
 *     unattributed attach ran under.
 *   - `matches-court` — an ATTRIBUTED court whose canonical key equals the
 *     filing court's. Only a binding carrying a provenance can reach this state.
 *   - `foreign-court` — a recorded court that names a DIFFERENT county in the
 *     filing court's own value space. Andrew's Madera-under-Marin repro.
 *   - `unrecognized-court` — a recorded court that is not the filing court and
 *     is not a county identity at all, so the two are not comparable and no
 *     verdict is available. See the value-space note below.
 *
 * Why provenance exists (SOR-191). Before SOR-182 this field held the county the
 * LOOKUP ran under, stamped from the filing county at attach time — so it
 * equalled the filing court BY CONSTRUCTION and carried no information about the
 * case's real court. SOR-182 repointed the field in place to the record's own
 * court, leaving the two meanings sharing one string slot: a pre-repoint draft
 * canonicalizes equal to its filing court and would classify `matches-court`,
 * indistinguishable from a server-asserted binding. Andrew's repro therefore
 * still advanced and submitted on any draft saved before the repoint. Reading
 * such a value as `absent` is what makes the two distinguishable: the draft
 * still grace-passes (nothing saved is retroactively invalidated, so the
 * backward-compatibility pin holds) but no longer ASSERTS same-court, so a
 * fail-closed gate keying on `absent` can recover the distinction later.
 * Without it the distinction is unrecoverable — a legacy echo is not absent.
 *
 * What the discriminator gates is ATTRIBUTION, not the case record specifically
 * (SOR-192). A court may return a record carrying no court of its own — Madera
 * answers `<CaseCourt xsi:nil="true"/>` on every GetCase — and reading every
 * such attach as `absent` fails the confirm flow closed on the entire
 * subsequent-filing population, with no re-attach that can repair it. So the
 * server attributes those to the court whose endpoint SERVED the lookup
 * (`'served-court'`), which is a fact about where the record came from rather
 * than a restatement of what was asked for. Both members are therefore
 * attributions and both may claim `matches-court`; the legacy echo is still the
 * one thing that cannot, because it carries no provenance at all.
 *
 * The provenance leg is deliberately ASYMMETRIC — it gates only the positive
 * claim, and the order of the checks below is what implements that. Absence of
 * attribution is absence of evidence that the case belongs to the filing court,
 * so it must not assert `matches-court`. It is NOT absence of evidence for the
 * negative: an echo that DIFFERS from the filing court still says the attach ran
 * under some other county, which is exactly the misfiling SOR-154 added this
 * rule to block. Sending every unattributed value to `absent` would quietly
 * un-block that population while fixing the adjacent defect, so a differing
 * value stays `foreign-court` whatever its provenance.
 *
 * Why `unrecognized-court` exists — the VALUE SPACE is not settled on the wire.
 * The recorded court reaches us from JTI's GetCase response at
 * `CaseResponseMessage/CaseCourt/OrganizationIdentification/IdentificationID`,
 * and that element is filled with a county code on the REQUEST side but its
 * response value space has never been captured live. JTI's own published
 * samples put a deployment hostname there
 * (`efm-placer-court-prod.ecourt.com`, `certification/samples/
 * fam_sub_firstpaper_custoryorvisit_1613769912.xml:24`) alongside
 * `IdentificationSourceText`/`CourtName` in two further value spaces. Comparing
 * such a value against a county display name with `!==` yields "different" for
 * EVERY case — including every legitimate same-court subsequent filing — so a
 * two-state classifier would have hard-blocked the whole subsequent-filing
 * population in BOTH flows the moment a real court answered.
 *
 * This third state is what keeps that outcome unreachable: an accusation is
 * made only between two values that are actually comparable, and a value we
 * cannot place yields the pre-SOR-182 verdict (nothing fires, the draft
 * grace-passes) rather than a guess in either direction. The trade is explicit
 * and recorded: while the recorded court is outside the county value space the
 * cross-court protection is INERT for that court, exactly as it was before this
 * work. Capturing one live GetCase response and mapping its value space onto a
 * county — the one-line change is `COUNTY_IDENTITY_KEYS` above, or a resolver
 * feeding it — turns the whole population from `unrecognized-court` into
 * `matches-court`/`foreign-court` with no change to any gate that consumes this.
 * The api-gateway logs every court identity it cannot place (`get_case_court_
 * identity_unrecognized`) so the first real dev/qa lookup settles it.
 *
 * Quote the scope of that protection honestly. SPEC-SOR-174's headline is that
 * the cross-court refusal "covers the whole class" — Riverside-into-San-
 * Bernardino, not just Andrew's Marin/Madera repro — and that holds for every
 * court whose recorded identity IS a county identity, which is a real class and
 * not a single case. It does NOT hold for a court answering outside that value
 * space: there this returns `unrecognized-court` and no gate fires. **No test
 * can currently tell the two apart** — the unit pins and the E2E repro both
 * stamp a county-space value, so they exercise the enforcing branch by
 * construction and would pass unchanged if the live value space turned out to be
 * deployment hostnames. So while the response value space stays uncaptured,
 * read the promise as scoped to the county value space. Capturing it is the
 * whole remaining work and it is a DATA change, not a code change.
 *
 * A recorded county against an unset filing court cannot be `matches-court` (the
 * keys differ), so it classifies `foreign-court`. The filer never sees it as the
 * presenting error: `county` is required by `validateOrderInfo` and that rule
 * surfaces first.
 */
export function caseCourtBinding(binding, filingCourt) {
    const recorded = binding.caseSourceCounty ? courtIdentityKey(binding.caseSourceCounty) : '';
    if (!recorded)
        return 'absent';
    const filing = filingCourt ? courtIdentityKey(filingCourt) : '';
    // Disagreement accuses regardless of provenance (see the asymmetry note) —
    // but only between two values in the same space. An unplaceable identity is
    // "we cannot tell", never "a different court".
    if (recorded !== filing) {
        return isCountyIdentityKey(recorded) ? 'foreign-court' : 'unrecognized-court';
    }
    // The keys agree — but only an ATTRIBUTED binding may claim same-court. An
    // unattributed value agreeing with the filing court is the pre-SOR-182 echo,
    // which agrees BY CONSTRUCTION and so is evidence of nothing.
    return isAttributed(binding.caseSourceCountyProvenance) ? 'matches-court' : 'absent';
}
